import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

// Public web page where users (including those who uninstalled the app) can
// request account + data deletion. This URL is registered with Google Play
// Console under Data Safety, so it must remain accessible at
// https://quattro-game.com/delete-account forever.

const APP_NAME = "Quattro";
const COMPANY = "Quattro Games";
const SUPPORT_EMAIL = "support@quattro-game.com";

export default function DeleteAccount() {
  const nav = useNavigate();
  const [copied, setCopied] = useState(false);

  const emailBody = encodeURIComponent(
    `Hi ${COMPANY} support team,\n\nI'd like to request the deletion of my ${APP_NAME} account and all associated data.\n\nMy email or Google account used in the app: \n[Please fill in]\n\nMy in-game username (if known): \n[Please fill in]\n\nThank you.`
  );
  const emailSubject = encodeURIComponent(`Account & data deletion request — ${APP_NAME}`);
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${emailSubject}&body=${emailBody}`;

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {/* ignore */}
  };

  return (
    <div className="min-h-screen w-full px-4 md:px-8 py-8" data-testid="delete-account-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            className="btn-ghost"
            onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
            data-testid="delete-account-back-btn"
          >
            Back
          </button>
          <h1 className="font-display font-black text-2xl md:text-3xl">
            Delete your {APP_NAME} account & data
          </h1>
        </div>

        <article className="prose prose-invert space-y-5 text-sm text-white/80">
          <p>
            You can permanently delete your {APP_NAME} account and the data we
            store about it at any time. This page explains the two ways to do it,
            and exactly what will be erased.
          </p>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">
            Option 1 — Inside the app (fastest, instant)
          </h2>
          <ol className="list-decimal list-inside space-y-1.5">
            <li>Open the {APP_NAME} app.</li>
            <li>Tap the <b>Profile</b> icon (bottom of the lobby).</li>
            <li>Scroll to the bottom and tap <b>Delete Account</b>.</li>
            <li>Confirm in the prompt. Your account is erased immediately.</li>
          </ol>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">
            Option 2 — By email (use this if you've uninstalled the app)
          </h2>
          <p>Send an email to:</p>
          <div className="flex flex-wrap items-center gap-2">
            <code
              className="px-3 py-1.5 rounded-md bg-white/8 text-white font-mono text-sm"
              data-testid="delete-account-email"
            >
              {SUPPORT_EMAIL}
            </code>
            <button
              onClick={copyEmail}
              className="btn-ghost text-xs px-3 py-1.5"
              data-testid="delete-account-copy-email"
            >
              {copied ? "Copied!" : "Copy"}
            </button>
            <a
              href={mailto}
              className="btn-primary text-xs px-3 py-1.5"
              data-testid="delete-account-mailto"
            >
              Open email app
            </a>
          </div>
          <p className="text-white/55 text-xs">
            Include the Google email / username you used in {APP_NAME} so we can
            find your account. We respond within <b>30 days</b>; for most
            requests it's within 24 hours.
          </p>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">
            What gets deleted
          </h2>
          <ul className="list-disc list-inside space-y-1">
            <li>Your profile (display name, avatar, country)</li>
            <li>Your chip balance, gem balance, and cosmetics inventory</li>
            <li>Your match history, win streak, level, and tournament stats</li>
            <li>Your friends list and head-to-head records</li>
            <li>Any push notification tokens linked to your account</li>
            <li>Your Google OAuth association (you can re-sign in fresh anytime)</li>
          </ul>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">
            What we keep
          </h2>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <b>Purchase records</b> — invoice metadata (date, amount, currency,
              IAP product ID) we are required to retain for accounting, tax, and
              fraud-prevention purposes. No personal info beyond an anonymous
              transaction ID is kept. Retention: 7 years (legal requirement).
            </li>
            <li>
              <b>Aggregated, fully anonymous gameplay statistics</b> (e.g. "total
              matches played per day across all players"). These contain no
              identifiers and cannot be linked back to you.
            </li>
          </ul>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">
            How long it takes
          </h2>
          <p>
            <b>Option 1 (in-app)</b>: instant — your account is erased the
            moment you tap confirm.<br />
            <b>Option 2 (email)</b>: typically within 24 hours, max 30 days, as
            required by GDPR.
          </p>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">
            Questions
          </h2>
          <p>
            If you can't find the in-app Delete Account button, or want a
            confirmation that your data was erased, just email{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-amber-300 underline"
            >
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </article>
      </div>
    </div>
  );
}
